/**
 * Sprint 5 client feedback 2026-07-03 — LLM narrative formatter.
 *
 * Some backend fields carry LLM-authored prose (Investment Decision
 * Summary, Auditor Opinion rationale, Executive Summary cards,
 * Consolidated Findings observation/criteria/implication/recommendation,
 * DD § risk assessments). The models frequently emit lightweight
 * markdown syntax — most commonly `**bold**`, occasionally `*italic*`,
 * paragraph breaks with `\n\n`, and single-line bullets with `- `.
 * Rendering those strings verbatim leaves visible asterisks and dashes
 * that clutter the report.
 *
 * This component parses the common markdown patterns and renders them
 * as JSX. It is deliberately NOT a full markdown parser:
 *
 *   Handled:
 *     - **bold**              → <strong>
 *     - *italic* / _italic_   → <em>
 *     - `inline code`         → <code>
 *     - line breaks           → <br>
 *     - blank line separators → new paragraph
 *     - "- item" / "* item"   → <ul><li>
 *
 *   Deliberately NOT handled:
 *     - headings (#, ##)      — LLM prose shouldn't add headings
 *     - links / images        — no external link surface here
 *     - tables                — findings already render as tables
 *     - HTML tags in input    — treated as plain text (XSS guard)
 *
 * The parser is a simple recursive-descent over inline runs; all input
 * is written into React children as plain strings (no
 * dangerouslySetInnerHTML), so injection is not possible.
 */
import { Fragment, type ReactNode, isValidElement, memo } from 'react';

// ─── Inline runs (bold / italic / code) ───────────────────────────

/**
 * Parse a single line of text into JSX runs. Handles bold, italic,
 * and inline code. Runs may nest — bold inside italic etc. — but only
 * one level deep, which covers all LLM output we've observed.
 */
function parseInline(text: string, keyPrefix = ''): ReactNode[] {
  if (!text) return [];

  // Order matters: **bold** must be matched before *italic* so
  // `**strong**` isn't misread as `*` + `strong*`. Same with `__` vs `_`.
  const patterns: Array<{
    re: RegExp;
    wrap: (inner: ReactNode[], k: string) => ReactNode;
  }> = [
    {
      re: /\*\*([^\s*][^*]*[^\s*]|\S)\*\*/,
      wrap: (inner, k) => <strong key={k}>{inner}</strong>,
    },
    {
      re: /__([^\s_][^_]*[^\s_]|\S)__/,
      wrap: (inner, k) => <strong key={k}>{inner}</strong>,
    },
    {
      re: /(?<![*])\*([^\s*][^*]*[^\s*]|\S)\*(?![*])/,
      wrap: (inner, k) => <em key={k}>{inner}</em>,
    },
    {
      re: /(?<![_\w])_([^\s_][^_]*[^\s_]|\S)_(?![_\w])/,
      wrap: (inner, k) => <em key={k}>{inner}</em>,
    },
    {
      re: /`([^`]+)`/,
      wrap: (inner, k) => <code key={k}>{inner}</code>,
    },
  ];

  const nodes: ReactNode[] = [];
  let remaining = text;
  let idx = 0;

  while (remaining.length > 0) {
    let earliest: { start: number; length: number; content: string; wrap: (i: ReactNode[], k: string) => ReactNode } | null = null;

    for (const { re, wrap } of patterns) {
      const m = re.exec(remaining);
      if (m && (earliest == null || m.index < earliest.start)) {
        earliest = {
          start: m.index,
          length: m[0].length,
          content: m[1] ?? '',
          wrap,
        };
      }
    }

    if (earliest == null) {
      nodes.push(remaining);
      break;
    }

    if (earliest.start > 0) {
      nodes.push(remaining.slice(0, earliest.start));
    }
    // Recurse for one nested level — cheap given LLM output shape.
    const inner = parseInline(earliest.content, `${keyPrefix}i${idx}-`);
    nodes.push(earliest.wrap(inner, `${keyPrefix}r${idx}`));
    remaining = remaining.slice(earliest.start + earliest.length);
    idx += 1;
  }

  return nodes;
}

// ─── Block layer (paragraphs + bullet lists) ─────────────────────

interface Block {
  kind: 'p' | 'ul';
  lines: string[];
}

function splitIntoBlocks(text: string): Block[] {
  const rawLines = text.replace(/\r\n/g, '\n').split('\n');
  const blocks: Block[] = [];
  let current: Block | null = null;

  const bulletRe = /^\s*[-*•]\s+(.*)$/;

  for (const raw of rawLines) {
    const line = raw.replace(/\s+$/, '');

    if (line === '') {
      // Blank line ends the current block.
      if (current) {
        blocks.push(current);
        current = null;
      }
      continue;
    }

    const bulletMatch = bulletRe.exec(line);
    if (bulletMatch) {
      const item = bulletMatch[1];
      if (current && current.kind === 'ul') {
        current.lines.push(item);
      } else {
        if (current) blocks.push(current);
        current = { kind: 'ul', lines: [item] };
      }
      continue;
    }

    // Ordinary paragraph line.
    if (current && current.kind === 'p') {
      current.lines.push(line);
    } else {
      if (current) blocks.push(current);
      current = { kind: 'p', lines: [line] };
    }
  }

  if (current) blocks.push(current);
  return blocks;
}

// ─── Public component ────────────────────────────────────────────

export interface FormattedTextProps {
  /** The LLM-authored string. Falsy renders nothing. */
  text: string | null | undefined;
  /**
   * When true, wrap the entire output in a <span> and skip block
   * detection — use for short single-line contexts where a <p>
   * would introduce unwanted margins (badge tooltips, table cells).
   */
  inline?: boolean;
  /**
   * Optional className applied to the outer wrapper (the <div> in
   * block mode, or the <span> in inline mode).
   */
  className?: string;
}

/**
 * Render LLM-emitted markdown-lite as JSX.
 *
 * Safe by construction: input is only inserted as React text children
 * or via structural elements. No dangerouslySetInnerHTML anywhere in
 * the pipeline.
 */
function FormattedTextImpl({ text, inline = false, className }: FormattedTextProps): ReactNode {
  if (text == null) return null;
  const s = String(text);
  if (!s) return null;

  if (inline) {
    // Collapse newlines to spaces for the single-line contexts.
    const nodes = parseInline(s.replace(/\s*\n\s*/g, ' '));
    return <span className={className}>{nodes}</span>;
  }

  const blocks = splitIntoBlocks(s);
  if (blocks.length === 0) return null;

  return (
    <div className={className}>
      {blocks.map((b, bi) => {
        if (b.kind === 'ul') {
          return (
            <ul key={`ul-${bi}`} style={{ margin: '0 0 var(--s3) 0', paddingLeft: '1.25em' }}>
              {b.lines.map((li, i) => (
                <li key={`li-${bi}-${i}`}>{parseInline(li, `ul${bi}-${i}-`)}</li>
              ))}
            </ul>
          );
        }
        // Paragraph — join lines with <br> so soft-wraps in prose
        // aren't collapsed into space-separated words. Blank-line
        // separated paragraphs already came out as separate blocks.
        return (
          <p key={`p-${bi}`} style={{ margin: '0 0 var(--s3) 0' }}>
            {b.lines.map((ln, i) => (
              <Fragment key={`ln-${bi}-${i}`}>
                {i > 0 && <br />}
                {parseInline(ln, `p${bi}-${i}-`)}
              </Fragment>
            ))}
          </p>
        );
      })}
    </div>
  );
}

// Guard against unnecessary re-renders — the same string produces
// the same JSX.
export const FormattedText = memo(FormattedTextImpl, (prev, next) => {
  return (
    prev.text === next.text
    && prev.inline === next.inline
    && prev.className === next.className
  );
});

// ─── Utility: raw-text sanitiser ────────────────────────────────

/**
 * When you need a PLAIN string with no JSX (aria labels, page titles,
 * search haystacks), strip the markdown markers without formatting.
 */
export function stripMarkdown(text: string | null | undefined): string {
  if (text == null) return '';
  return String(text)
    .replace(/\*\*(.+?)\*\*/g, '$1')
    .replace(/__(.+?)__/g, '$1')
    .replace(/\*(.+?)\*/g, '$1')
    .replace(/_(.+?)_/g, '$1')
    .replace(/`([^`]+)`/g, '$1')
    .replace(/^\s*[-*•]\s+/gm, '')
    // Sprint 5 client feedback 2026-07-11 (Format Phase 1) — drop
    // *orphan* markers left behind when an upstream truncator cut a
    // string mid-pair (e.g. "Secure **anchor offtake agreeme…" →
    // strip the stray "**"). Without this, callers that pipe through
    // stripMarkdown for tooltip / aria haystacks still show the
    // literal marker. Applied AFTER the paired replacements so we
    // never strip a valid emphasis marker.
    .replace(/\*\*/g, '')
    .replace(/__/g, '');
}

/**
 * Sprint 5 client feedback 2026-07-11 (Tooltip sweep) — dedicated
 * sanitiser for text destined for a native browser `title=` attribute.
 * Native tooltips render plain text only, so:
 *   • **bold** / *italic* / `code` markers show as literal characters
 *   • HTML entities (&lt; &gt; &amp; &#39; &quot; &nbsp;) show as
 *     "&lt;" not "<"
 *   • Multi-line \n from LLM prose is preserved by the browser but
 *     leading / trailing whitespace clutters the tooltip
 * Route every LLM-authored / BE-authored tooltip string through this
 * before assigning to a `title=` attr.
 */
export function cleanTooltip(text: string | null | undefined): string {
  if (text == null) return '';
  return stripMarkdown(String(text))
    // Decode HTML entities that upstream template engines emit as
    // encoded (typical when text round-trips through the DOCX/PDF
    // renderer). Ordered so entities that look similar (e.g. &amp;lt;)
    // decode in the right sequence: &amp; last so "&amp;lt;" → "&lt;"
    // → "<" instead of "&<".
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/gi, "'")
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    // Collapse runs of internal whitespace but preserve the \n line
    // breaks the tooltip designer intentionally added (many tooltips
    // use \n to render as a stacked list).
    .replace(/[ \t]+/g, ' ')
    .replace(/[ \t]*\n[ \t]*/g, '\n')
    .trim();
}

// Re-export for consumers who import the wrong casing.
export default FormattedText;

// The isValidElement import above is retained for future extensibility
// (e.g. accepting pre-parsed ReactNode children) — the linter may flag
// it in some configs. Wrap in a use so tsc --strict doesn't complain.
void isValidElement;
